import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, ExternalLink, Globe, Loader2, Mail, MapPin, Star, Users, X, Zap } from "lucide-react";
import {
    approveEventEnrichment,
    approveOwnerLinkedin,
    BOOST_EVENT_TYPES,
    BoostEntry,
    BoostField,
    BoostState,
    BoostStep,
    getBoostState,
    rejectEventEnrichment,
    rejectOwnerLinkedin,
    startBoost,
} from "../../interfaces/enrichmentBoost";
import { LinkedEstablishment } from "../../interfaces/restaurantNews";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { LinkedInLogo } from "../icons/LinkedInLogo";
import { ownerDisplayName } from "../../lib/personName";

/**
 * Admin-only « Booster l'enrichissement » popup, opened from the news panel: pick the fields,
 * launch, and watch each one come back live. Nothing here bypasses the review — a result with
 * several candidates waits « à vérifier » (it is also in the BO review queues), and every result
 * can be approved or refused from here at any time, whatever its state.
 *
 * The backend skips every pipeline gate (switches, quota, window, NAF, cooldowns) but still
 * counts each call in the quota. Google and LinkedIn go through DataForSEO's live endpoints.
 */

const POLL_MS = 1500;
/** Candidates shown before « Voir les N candidats ». */
const OPTIONS_SHOWN = 3;

const FIELDS: Array<{ id: BoostField; label: string; hint: string; icon: React.ElementType }> = [
    { id: "owners", label: "Dirigeants", hint: "Registre (RNE) — gérant, qualité, âge", icon: Users },
    { id: "google", label: "Fiche Google", hint: "Téléphone, note, site, adresse — DataForSEO live", icon: MapPin },
    { id: "website", label: "Site web", hint: "Domaines candidats vérifiés", icon: Globe },
    { id: "email", label: "Email", hint: "Emails du site validé — Outscraper", icon: Mail },
    { id: "linkedin", label: "LinkedIn des dirigeants", hint: "Profils candidats — DataForSEO live", icon: LinkedInLogo },
];

const FOCUS_RING = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

/** The fields this sheet is missing — what the popup pre-selects. */
function missingFields(est: LinkedEstablishment): BoostField[] {
    const owners = est.owners ?? [];
    const missing: BoostField[] = [];
    if (owners.length === 0) missing.push("owners");
    if (!est.phone || est.ratings?.google_avg == null) missing.push("google");
    if (!est.website_url) missing.push("website");
    if (!est.email) missing.push("email");
    if (owners.length === 0 || owners.some((o) => !o.linkedinUrl)) missing.push("linkedin");
    return missing;
}

const isLive = (entry: BoostEntry | null | undefined) => entry?.status === "processing" || entry?.status === "waiting_callback";

function errorMessage(e: any): string {
    return e?.response?.data?.error || e?.message || "Erreur";
}

// ─── Status pill ──────────────────────────────────────────────────────────────

function StatusPill({ step, entry }: { step?: BoostStep; entry?: BoostEntry | null }) {
    if (step?.status === "running" || isLive(entry)) {
        return (
            <Badge tone="info" className="!px-2 !py-0.5 !text-[11px]">
                <Loader2 className="h-3 w-3 animate-spin" />
                Recherche…
            </Badge>
        );
    }
    if (entry?.status === "approved") return <Badge tone="success" className="!px-2 !py-0.5 !text-[11px]"><Check className="h-3 w-3" />Validé</Badge>;
    if (entry?.status === "done") {
        const several = (entry.options?.length ?? 0) > 1;
        return <Badge tone="warning" className="!px-2 !py-0.5 !text-[11px]">{several ? "Plusieurs choix — à vérifier" : "À valider"}</Badge>;
    }
    if (step?.status === "error") return <Badge tone="danger" className="!px-2 !py-0.5 !text-[11px]">Erreur</Badge>;
    if (step?.status === "blocked") return <Badge tone="neutral" className="!px-2 !py-0.5 !text-[11px]">En attente</Badge>;
    if (entry?.status === "retry" && entry.isManuallyReviewed) return <Badge tone="neutral" className="!px-2 !py-0.5 !text-[11px]">Refusé</Badge>;
    if (step?.status === "empty" || entry?.status === "retry") return <Badge tone="neutral" className="!px-2 !py-0.5 !text-[11px]">Aucun résultat</Badge>;
    if (step?.status === "done") return <Badge tone="success" className="!px-2 !py-0.5 !text-[11px]"><Check className="h-3 w-3" />Terminé</Badge>;
    return null;
}

// ─── Options ──────────────────────────────────────────────────────────────────

function GoogleOption({ o }: { o: any }) {
    const title = o.title || o.display_name || o.name;
    const address = o.address || o.address_info?.address || o.formatted_address;
    const rating = o.rating?.value ?? (typeof o.rating === "number" ? o.rating : null);
    const votes = o.rating?.votes_count ?? o.rating_count ?? null;
    const site = o.url || o.website || o.website_uri;
    const closed = o.work_time?.work_hours?.current_status === "closed_forever";
    return (
        <span className="block min-w-0">
            <span className="flex items-center gap-2">
                <span className="truncate font-medium text-ink">{title || "Sans nom"}</span>
                {closed && <Badge tone="danger" className="!px-1.5 !py-0 !text-[10px]">Fermé définitivement</Badge>}
            </span>
            {address && <span className="block truncate text-xs text-ink-muted">{address}</span>}
            <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-ink-muted">
                {o.phone && <span className="font-mono">{o.phone}</span>}
                {rating != null && (
                    <span className="inline-flex items-center gap-1">
                        <Star className="h-3 w-3 fill-warning text-warning" />
                        {Number(rating).toLocaleString("fr-FR", { maximumFractionDigits: 1 })}
                        {votes != null && <span>· {votes} avis</span>}
                    </span>
                )}
                {o.category && <span>{o.category}</span>}
                {site && <span className="truncate">{String(site).replace(/^https?:\/\/(www\.)?/, "")}</span>}
            </span>
        </span>
    );
}

function OptionBody({ field, o }: { field: BoostField; o: any }) {
    if (field === "google") return <GoogleOption o={o} />;
    if (field === "website") {
        return (
            <span className="block min-w-0">
                <span className="block truncate font-medium text-ink">{o.domain || o.url}</span>
                {o.title && <span className="block truncate text-xs text-ink-muted">{o.title}</span>}
            </span>
        );
    }
    if (field === "email") {
        return (
            <span className="block min-w-0">
                <span className="block truncate font-mono text-[13px] text-ink">{o.email || o.value}</span>
                {Array.isArray(o.sources) && o.sources.length > 0 && (
                    <span className="block truncate text-xs text-ink-muted">
                        {o.sources.length} source{o.sources.length > 1 ? "s" : ""} · {o.sources[0]?.ref}
                    </span>
                )}
            </span>
        );
    }
    return (
        <span className="block min-w-0">
            <span className="block truncate text-[13px] font-medium text-ink">{o.title || o.url}</span>
            <span className="block truncate text-xs text-ink-muted">{String(o.url).replace(/^https?:\/\/(\w+\.)?/, "")}</span>
            {o.snippet && <span className="mt-0.5 block line-clamp-2 text-xs text-ink-faint">{o.snippet}</span>}
        </span>
    );
}

/** Option URL to open in a new tab, when the candidate has one. */
function optionHref(field: BoostField, o: any): string | null {
    if (field === "website") return o.url || null;
    if (field === "linkedin") return o.url || null;
    if (field === "google") {
        if (o.place_id) return `https://www.google.com/maps/place/?q=place_id:${o.place_id}`;
        if (o.cid) return `https://www.google.com/maps?cid=${o.cid}`;
    }
    return null;
}

/** Index of the option the entry currently holds as its data, so the radio starts on it. */
function currentIndex(field: BoostField, entry: BoostEntry): number {
    const d = entry.data;
    if (!d) return 0;
    const key = (o: any) =>
        field === "google" ? o?.place_id || o?.cid || o?.title : field === "email" ? o?.email : o?.url || o?.domain;
    const i = entry.options.findIndex((o) => key(o) && key(o) === key(d));
    return i >= 0 ? i : 0;
}

/**
 * The candidates of one entry, with approve / refuse. Shown whatever the entry's status: an
 * approved result can be swapped for another candidate, a refused one approved after all.
 */
function Review({
    field,
    entry,
    busy,
    onApprove,
    onReject,
}: {
    field: BoostField;
    entry: BoostEntry;
    busy: boolean;
    onApprove: (option: any) => void;
    onReject: () => void;
}) {
    const [picked, setPicked] = useState(() => currentIndex(field, entry));
    const [expanded, setExpanded] = useState(false);
    // An approved field folds its candidates away; « Changer » opens them again.
    const [reopened, setReopened] = useState(false);
    const options = entry.options ?? [];
    useEffect(() => setPicked(currentIndex(field, entry)), [field, entry]);
    useEffect(() => setReopened(false), [entry.status]);
    if (options.length === 0) return null;

    if (entry.status === "approved" && !reopened) {
        return (
            <button
                type="button"
                onClick={() => setReopened(true)}
                className={`mt-1.5 rounded text-xs font-medium text-ink-muted underline underline-offset-2 hover:text-ink ${FOCUS_RING}`}
            >
                Changer ({options.length} candidat{options.length > 1 ? "s" : ""})
            </button>
        );
    }

    const canReject = entry.status === "done" || entry.status === "approved";
    // The picked candidate always stays visible, even past the fold.
    const shown = options
        .map((o, i) => ({ o, i }))
        .filter(({ i }) => expanded || i < OPTIONS_SHOWN || i === picked);
    return (
        <div className="mt-2.5">
            <ul className="space-y-1.5">
                {shown.map(({ o, i }) => {
                    const href = optionHref(field, o);
                    return (
                        <li key={i}>
                            <label
                                className={`flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 transition-colors ${
                                    picked === i ? "border-ink bg-surface-sunken/60" : "border-border hover:border-border-strong"
                                }`}
                            >
                                <input
                                    type="radio"
                                    className="mt-1 h-3.5 w-3.5 shrink-0 accent-ink"
                                    checked={picked === i}
                                    onChange={() => setPicked(i)}
                                />
                                <span className="min-w-0 flex-1 font-sans text-sm">
                                    <OptionBody field={field} o={o} />
                                </span>
                                {href && (
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        title="Ouvrir"
                                        className={`mt-0.5 shrink-0 rounded text-ink-faint hover:text-ink ${FOCUS_RING}`}
                                    >
                                        <ExternalLink className="h-3.5 w-3.5" />
                                    </a>
                                )}
                            </label>
                        </li>
                    );
                })}
            </ul>
            <div className="mt-2 flex items-center justify-end gap-2">
                {options.length > shown.length && (
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className={`mr-auto rounded text-xs font-medium text-ink-muted hover:text-ink ${FOCUS_RING}`}
                    >
                        Voir les {options.length} candidats
                    </button>
                )}
                <Button variant="secondary" size="sm" onClick={onReject} disabled={busy || !canReject} title={canReject ? undefined : "Rien à refuser"}>
                    <X className="h-4 w-4" />
                    Refuser
                </Button>
                <Button size="sm" onClick={() => onApprove(options[picked])} disabled={busy}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    {entry.status === "approved" ? "Valider ce choix" : "Valider"}
                </Button>
            </div>
        </div>
    );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export function EnrichmentBoostModal({
    establishment,
    onClose,
    onUpdated,
}: {
    establishment: LinkedEstablishment;
    onClose: () => void;
    /** The sheet as it stands after each refresh, so the news panel repaints with it. */
    onUpdated: (est: LinkedEstablishment) => void;
}) {
    const estId = establishment._id;
    const [state, setState] = useState<BoostState | null>(null);
    const [selected, setSelected] = useState<Set<BoostField>>(() => new Set(missingFields(establishment)));
    const [launching, setLaunching] = useState(false);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    // Email waits for a validated website; once one shows up it is relaunched on its own, once.
    const emailWanted = useRef(false);
    const onUpdatedRef = useRef(onUpdated);
    onUpdatedRef.current = onUpdated;

    const apply = useCallback((next: BoostState) => {
        setState(next);
        if (next.establishment) onUpdatedRef.current(next.establishment);
    }, []);

    const refresh = useCallback(async () => {
        try {
            apply(await getBoostState(estId));
        } catch (e) {
            setError(errorMessage(e));
        }
    }, [estId, apply]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const steps = state?.run?.steps ?? {};
    const people = state?.people ?? [];
    const running =
        Object.values(steps).some((s) => s?.status === "running") ||
        Object.values(state?.fields ?? {}).some(isLive) ||
        people.some((p) => isLive(p.linkedin));

    // Live: poll while anything is in flight.
    useEffect(() => {
        if (!running) return;
        const t = setTimeout(refresh, POLL_MS);
        return () => clearTimeout(t);
    }, [running, state, refresh]);

    const launch = async (fields: BoostField[]) => {
        if (fields.length === 0) return;
        setLaunching(true);
        setError(null);
        if (fields.includes("email")) emailWanted.current = true;
        try {
            const res = await startBoost(estId, fields);
            apply(res.state);
            if (res.skipped.length) setError(res.skipped.map((s) => `${FIELDS.find((f) => f.id === s.field)?.label} : ${s.reason}`).join(" · "));
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setLaunching(false);
        }
    };

    useEffect(() => {
        if (emailWanted.current && state?.website && steps.email?.status === "blocked") {
            emailWanted.current = false;
            launch(["email"]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state?.website, steps.email?.status]);

    const review = async (key: string, action: () => Promise<BoostState | void>) => {
        setBusyKey(key);
        setError(null);
        try {
            const next = await action();
            if (next) apply(next);
            else await refresh();
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusyKey(null);
        }
    };

    const toggle = (field: BoostField) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(field)) next.delete(field);
            else next.add(field);
            return next;
        });

    const est = state?.establishment ?? establishment;
    const owners = est.owners ?? [];

    const current = (field: BoostField): React.ReactNode => {
        if (field === "owners")
            return owners.length ? owners.map((o) => `${ownerDisplayName(o.name)}${o.age != null ? ` (${o.age} ans)` : ""}`).join(", ") : null;
        if (field === "google")
            return est.phone || est.ratings?.google_avg != null
                ? [est.phone, est.ratings?.google_avg != null ? `★ ${est.ratings.google_avg.toLocaleString("fr-FR")} (${est.ratings.google_count ?? 0} avis)` : null]
                      .filter(Boolean)
                      .join(" · ")
                : null;
        if (field === "website") return est.website_url ? String(est.website_url).replace(/^https?:\/\/(www\.)?/, "") : null;
        if (field === "email") return est.email ?? null;
        const withLi = owners.filter((o) => o.linkedinUrl).length;
        return owners.length ? `${withLi}/${owners.length} validé${withLi > 1 ? "s" : ""}` : null;
    };

    const eventField = (field: "google" | "website" | "email") => {
        const entry = state?.fields[field];
        const eventId = state?.eventId;
        if (!entry || !eventId) return null;
        return (
            <Review
                field={field}
                entry={entry}
                busy={busyKey === field}
                onApprove={(o) => review(field, () => approveEventEnrichment(eventId, BOOST_EVENT_TYPES[field], o))}
                onReject={() => review(field, () => rejectEventEnrichment(eventId, BOOST_EVENT_TYPES[field]))}
            />
        );
    };

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-sans" role="dialog" aria-modal="true" aria-label="Booster l'enrichissement">
            <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
            <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-border bg-surface shadow-float">
                <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
                    <div className="min-w-0">
                        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
                            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-danger text-white">
                                <Zap className="h-3.5 w-3.5" />
                            </span>
                            <span className="truncate">Booster l'enrichissement · {est.name}</span>
                        </h2>
                        <p className="mt-1 text-xs text-ink-muted">
                            Admin · toutes les restrictions levées, chaque appel compte dans les quotas
                            {state?.siret ? ` · SIRET ${state.siret}` : state?.siren ? ` · SIREN ${state.siren}` : ""}
                        </p>
                    </div>
                    <button onClick={onClose} aria-label="Fermer" className={`rounded p-1 text-ink-faint transition-colors hover:text-ink ${FOCUS_RING}`}>
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="overflow-y-auto px-5 py-3">
                    {state === null && !error ? (
                        <div className="flex items-center justify-center gap-2 py-12 text-sm text-ink-muted">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Chargement…
                        </div>
                    ) : (
                        <ul className="divide-y divide-border">
                            {FIELDS.map(({ id, label, hint, icon: Icon }) => {
                                const step = steps[id];
                                const entry = id === "google" || id === "website" || id === "email" ? state?.fields[id] : undefined;
                                const value = current(id);
                                return (
                                    <li key={id} className="py-3">
                                        <div className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                aria-label={label}
                                                checked={selected.has(id)}
                                                onChange={() => toggle(id)}
                                                className="mt-1 h-4 w-4 shrink-0 rounded-sm accent-danger"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <Icon className="h-4 w-4 shrink-0 text-ink-muted" />
                                                    <span className="text-sm font-medium text-ink">{label}</span>
                                                    <StatusPill step={step} entry={entry} />
                                                    <button
                                                        type="button"
                                                        onClick={() => launch([id])}
                                                        disabled={launching || step?.status === "running"}
                                                        className={`ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-danger transition-colors hover:bg-danger-subtle disabled:opacity-40 ${FOCUS_RING}`}
                                                    >
                                                        <Zap className="h-3 w-3" />
                                                        {step ? "Relancer" : "Lancer"}
                                                    </button>
                                                </div>
                                                <p className="mt-0.5 text-xs text-ink-faint">{hint}</p>
                                                <p className="mt-1 text-xs text-ink-muted">
                                                    <span className="text-ink-faint">Actuel : </span>
                                                    {value ?? <span className="italic text-ink-faint">vide</span>}
                                                </p>
                                                {step?.message && step.status !== "running" && entry?.status !== "approved" && (
                                                    <p className={`mt-1 flex items-start gap-1 text-xs ${step.status === "error" ? "text-danger" : "text-ink-muted"}`}>
                                                        {step.status === "error" && <AlertTriangle className="mt-px h-3 w-3 shrink-0" />}
                                                        {step.message}
                                                    </p>
                                                )}
                                                {entry?.searchKeyword && id === "google" && (
                                                    <p className="mt-0.5 truncate text-[11px] text-ink-faint" title={entry.searchKeyword}>
                                                        Recherche : « {entry.searchKeyword} »
                                                    </p>
                                                )}

                                                {(id === "google" || id === "website" || id === "email") && eventField(id)}

                                                {id === "linkedin" && people.length > 0 && (
                                                    <ul className="mt-2 space-y-3">
                                                        {people.map((p) => (
                                                            <li key={p._id} className="rounded-lg border border-border px-3 py-2">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <span className="text-sm font-medium text-ink">{ownerDisplayName(p.name)}</span>
                                                                    {p.qualite && <span className="text-xs text-ink-muted">{p.qualite}</span>}
                                                                    <StatusPill entry={p.linkedin} />
                                                                </div>
                                                                {p.linkedin?.error && !isLive(p.linkedin) && p.linkedin.status !== "done" && p.linkedin.status !== "approved" && (
                                                                    <p className="mt-1 text-xs text-ink-muted">{p.linkedin.error}</p>
                                                                )}
                                                                {p.linkedin && (
                                                                    <Review
                                                                        field="linkedin"
                                                                        entry={p.linkedin}
                                                                        busy={busyKey === `li:${p._id}`}
                                                                        onApprove={(o) => review(`li:${p._id}`, () => approveOwnerLinkedin(estId, p._id, o))}
                                                                        onReject={() => review(`li:${p._id}`, () => rejectOwnerLinkedin(estId, p._id))}
                                                                    />
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-3 border-t border-border px-5 py-3">
                    <button
                        type="button"
                        onClick={() => setSelected(selected.size === FIELDS.length ? new Set() : new Set(FIELDS.map((f) => f.id)))}
                        className={`rounded text-xs font-medium text-ink-muted hover:text-ink ${FOCUS_RING}`}
                    >
                        {selected.size === FIELDS.length ? "Tout désélectionner" : "Tout sélectionner"}
                    </button>
                    {error && <p className="min-w-0 flex-1 truncate text-xs text-danger" title={error}>{error}</p>}
                    <button
                        type="button"
                        onClick={() => launch(FIELDS.map((f) => f.id).filter((f) => selected.has(f)))}
                        disabled={launching || selected.size === 0}
                        className={`ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg bg-danger px-4 text-sm font-semibold text-white transition-colors hover:bg-danger/90 disabled:pointer-events-none disabled:opacity-40 ${FOCUS_RING}`}
                    >
                        {launching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                        Lancer le boost{selected.size > 0 ? ` (${selected.size})` : ""}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
